import { useEffect, useState } from 'react';
import { TrendingUp, AlertTriangle, Gauge } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getConsumptionRates, type ConsumptionRates } from '@/api/services/consumption.api';
import { toTitleCase, formatDate } from '@/lib/utils';

/** Rates carry more decimals than anyone reads; three is enough to compare. */
const rate = (n: number) => n.toFixed(3);

/**
 * Consumption per unit of work, and the records sitting above their benchmark.
 *
 * Twenty sacks means nothing on its own — sacks per stage, or barrels per foot,
 * is the figure that compares one well against another. This is the only place
 * the rate data is put to use rather than merely stored.
 */
export function RateBenchmarks() {
  const [data, setData] = useState<ConsumptionRates | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getConsumptionRates(365)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const { coverage, benchmarks, outliers } = data;
  const reliable = benchmarks.filter((b) => b.reliable);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-base font-semibold">Consumption per unit of work</h3>
          </div>
          {/* Coverage is part of the answer: a benchmark drawn from an eighth of
              the records is worth knowing about before it is acted on. */}
          <Badge
            variant="outline"
            className={
              coverage.percent >= 50
                ? 'border-status-success-border bg-status-success-subtle text-status-success-foreground'
                : 'border-status-warning-border bg-status-warning-subtle text-status-warning-foreground'
            }
          >
            {coverage.withRate} of {coverage.total} records carry a rate ({coverage.percent}%)
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {benchmarks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No consumption has been recorded with a base metric yet. Enter the work done, such as feet drilled or stages pumped, alongside the quantity, and the rate is derived from
            it.
          </p>
        ) : (
          <>
            {outliers.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-status-warning-foreground" />
                  <h4 className="text-sm font-semibold">Above benchmark</h4>
                </div>
                <div className="divide-y rounded-md border">
                  {outliers.slice(0, 5).map((o) => (
                    <div
                      key={o.recordId}
                      className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {o.sku}{' '}
                          <span className="font-normal text-muted-foreground">
                            {toTitleCase(o.operationPhase)}
                          </span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {o.job
                            ? `${o.job.jobNumber} · ${o.job.wellName ?? o.job.name}`
                            : 'No job'}{' '}
                          · {formatDate(o.consumedAt)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium tabular-nums">
                          {rate(o.rate)} {o.unit} {o.rateUnit}
                        </p>
                        <p className="text-xs text-status-warning-foreground">
                          {o.deviationPercent}% above {rate(o.averageRate)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-semibold">Benchmarks by item and phase</h4>
              </div>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Item</th>
                      <th className="px-3 py-2 text-left font-medium">Phase</th>
                      <th className="px-3 py-2 text-right font-medium">Average</th>
                      <th className="px-3 py-2 text-right font-medium">Range</th>
                      <th className="px-3 py-2 text-right font-medium">Observations</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {benchmarks.slice(0, 10).map((b) => (
                      <tr key={`${b.itemId}-${b.operationPhase}`}>
                        <td className="px-3 py-2">
                          <span className="font-medium">{b.sku}</span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {toTitleCase(b.operationPhase)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {rate(b.averageRate)} {b.unit} {b.rateUnit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {rate(b.minRate)} – {rate(b.maxRate)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {b.reliable ? (
                            <span className="tabular-nums">{b.observations}</span>
                          ) : (
                            // Saying "too few" is more use than a number that
                            // looks like a benchmark and is not one.
                            <span className="text-xs text-muted-foreground">
                              {b.observations} — too few
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reliable.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  None of these has enough observations to compare against yet. Three of the same
                  item in the same phase is the point at which an average starts to mean something.
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
